import * as React from "react";
import { Alert, Button } from "@mui/material";
import { createApi } from "../../../utils/createApi";
import { logger } from "../../../../utils/logger";
import {
  ScheduleOverrideRow,
  activeAndUpcomingOverrides,
  describeOverride,
  todayInTimezone,
} from "./SiteAboutPageUtils/scheduleOverrides";

/**
 * Active and upcoming schedule overrides for a site (ENG-2631).
 *
 * schedule/by_customer/<id>/ returns regular schedules unless is_override is
 * passed, so overrides need their own request. Fail-soft: any error (an
 * older admin, a rollout, a network blip) shows no notice and leaves the
 * Schedules tab exactly as it was. Never an error state.
 */
export const useScheduleOverrides = (
  siteId: number | string | undefined,
  timezone?: string
) => {
  const [rows, setRows] = React.useState<ScheduleOverrideRow[]>([]);
  const todayIso = todayInTimezone(timezone);

  React.useEffect(() => {
    if (!siteId) return;
    let cancelled = false;
    createApi(``)
      .get(`schedule/by_customer/${siteId}/`, {
        // Capitalised on purpose: by_customer hands these straight to the ORM
        // on admin before #3206, and Django's BooleanField accepts "True" but
        // 500s on "true". "True" also works after #3206 (DRF-style parsing).
        params: { is_override: "True", enabled: "True" },
      })
      .then((response: any) => {
        if (!cancelled)
          setRows(Array.isArray(response.data) ? response.data : []);
      })
      .catch((error: unknown) => {
        logger.error("schedule override lookup failed", error);
        if (!cancelled) setRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [siteId]);

  const overrides = React.useMemo(
    () => activeAndUpcomingOverrides(rows, todayIso),
    [rows, todayIso]
  );
  return { overrides, todayIso };
};

/** Overrides listed before "Show all" (a calendar can hold a dozen or more). */
export const OVERRIDE_LIST_LIMIT = 5;

export const ScheduleOverridesNotice = ({
  overrides,
  todayIso,
}: {
  overrides: ScheduleOverrideRow[];
  todayIso: string;
}) => {
  const [showAll, setShowAll] = React.useState(false);
  if (overrides.length === 0) return null;
  const described = overrides.map((row) => describeOverride(row, todayIso));
  const anyToday = described.some((d) => d.inEffectToday);
  // Earliest first, so anything in effect today is always in the short list.
  const shown = showAll ? described : described.slice(0, OVERRIDE_LIST_LIMIT);
  const hidden = described.length - shown.length;

  return (
    <Alert
      severity={anyToday ? "warning" : "info"}
      sx={{ fontFamily: "Mulish", mx: "15px", mt: "10px" }}
      data-testid="schedule-overrides-notice"
    >
      <p className="font-bold">
        {anyToday
          ? "Holiday schedule in effect today"
          : "Upcoming holiday schedule"}
      </p>
      <p className="pb-[4px]">
        On these dates the override replaces the regular schedule below: the
        site arms and disarms only as listed.
      </p>
      <ul className="list-disc pl-[20px]">
        {shown.map((d) => (
          <li key={d.id}>
            Holiday override · {d.dateLabel} · {d.windowLabel} · replaces the
            regular schedule
          </li>
        ))}
      </ul>
      {described.length > OVERRIDE_LIST_LIMIT && (
        <Button
          size="small"
          color="inherit"
          onClick={() => setShowAll((value) => !value)}
          sx={{ textTransform: "none", fontFamily: "Mulish", px: 0, mt: 0.5 }}
          data-testid="schedule-overrides-toggle"
        >
          {showAll
            ? "Show fewer"
            : `and ${hidden} more — show all ${described.length}`}
        </Button>
      )}
    </Alert>
  );
};
